import React, { useState, useEffect } from "react";
import {
    Box,
    Typography,
    Button,
    Fade,
    Container,
    Paper,
    Stack,
    useTheme,
    useMediaQuery
} from "@mui/material";
import { Explore, ShareLocation, VerifiedUser } from "@mui/icons-material";
import { AppLogo } from "./AppLogo";

export const LandingOverlay: React.FC = () => {
    const [open, setOpen] = useState(false);
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

    useEffect(() => {
        const hasSeenWelcome = localStorage.getItem("digipin_welcome_seen");
        if (!hasSeenWelcome) {
            // Small delay for smooth entrance
            const timer = setTimeout(() => setOpen(true), 500);
            return () => clearTimeout(timer);
        }
    }, []);

    const handleDismiss = () => {
        setOpen(false);
        localStorage.setItem("digipin_welcome_seen", "true");
    };

    if (!open) return null;

    return (
        <Fade in={open} timeout={800}>
            <Box
                sx={{
                    position: "fixed",
                    top: 0,
                    left: 0,
                    right: 0,
                    bottom: 0,
                    zIndex: 9999,
                    background: "rgba(0, 0, 0, 0.65)",
                    backdropFilter: "blur(12px)",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    p: 2,
                }}
            >
                <Paper
                    elevation={24}
                    sx={{
                        maxWidth: "800px",
                        width: "100%",
                        maxHeight: "90vh",
                        overflowY: "auto",
                        borderRadius: "24px",
                        background: "linear-gradient(145deg, rgba(20,20,25,0.95) 0%, rgba(10,10,15,0.98) 100%)",
                        border: "1px solid rgba(255, 255, 255, 0.1)",
                        boxShadow: "0 0 40px rgba(59, 130, 246, 0.15)",
                        p: { xs: 3, md: 6 },
                        textAlign: "left",
                        position: "relative",
                    }}
                >
                    {/* Header Section */}
                    <Box sx={{ display: "flex", alignItems: "center", gap: 2, mb: 4 }}>
                        <Box sx={{ p: 1, borderRadius: "16px", background: "rgba(255,255,255,0.05)" }}>
                            <AppLogo />
                        </Box>
                        <Typography variant="h4" component="h2" sx={{ fontWeight: 700, background: "linear-gradient(90deg, #fff, #9CA3AF)", WebkitBackgroundClip: "text", WebkitTextFillColor: "transparent" }}>
                            Digipin Explorer
                        </Typography>
                    </Box>

                    {/* Main Content / SEO Text */}
                    <Typography variant="h3" component="h1" sx={{
                        fontSize: { xs: "2rem", md: "2.75rem" },
                        fontWeight: 800,
                        mb: 3,
                        lineHeight: 1.1,
                        letterSpacing: "-0.02em"
                    }}>
                        The Future of <span style={{ color: theme.palette.primary.main }}>Digital Addressing</span> in India
                    </Typography>

                    <Typography variant="body1" sx={{ fontSize: "1.1rem", color: "text.secondary", mb: 5, lineHeight: 1.6 }}>
                        Convert any location in India into a short, precise digital address.
                        <strong> Digipin Explorer</strong> is the ultimate tool for logistics, emergency services, and everyday navigation.
                    </Typography>

                    {/* Features Grid */}
                    <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", md: "1fr 1fr" }, gap: 3, mb: 6 }}>
                        <Box sx={{ p: 2.5, borderRadius: "16px", background: "rgba(255,255,255,0.03)", border: "1px solid rgba(255,255,255,0.05)" }}>
                            <Stack direction="row" gap={2} alignItems="flex-start">
                                <Explore sx={{ color: theme.palette.primary.main, mt: 0.5 }} />
                                <Box>
                                    <Typography variant="h6" gutterBottom sx={{ color: "white" }}>Precision Mapping</Typography>
                                    <Typography variant="body2" color="text.secondary">
                                        Instantly convert <strong>Latitude and Longitude</strong> to Digipin and back. Accurate to 4x4 meters.
                                    </Typography>
                                </Box>
                            </Stack>
                        </Box>

                        <Box sx={{ p: 2.5, borderRadius: "16px", background: "rgba(255,255,255,0.03)", border: "1px solid rgba(255,255,255,0.05)" }}>
                            <Stack direction="row" gap={2} alignItems="flex-start">
                                <ShareLocation sx={{ color: theme.palette.secondary.main, mt: 0.5 }} />
                                <Box>
                                    <Typography variant="h6" gutterBottom sx={{ color: "white" }}>Universal Standard</Typography>
                                    <Typography variant="body2" color="text.secondary">
                                        The official <strong>National Digital Addressing System</strong> for seamless logistics and delivery across India.
                                    </Typography>
                                </Box>
                            </Stack>
                        </Box>
                    </Box>

                    {/* Hidden SEO Text Block - Moved to persistent SEOHelper component */}

                    {/* Action */}
                    <Button
                        variant="contained"
                        size="large"
                        onClick={handleDismiss}
                        sx={{
                            py: 2,
                            px: 6,
                            borderRadius: "12px",
                            fontSize: "1.1rem",
                            background: "linear-gradient(135deg, #3B82F6 0%, #2563EB 100%)",
                            boxShadow: "0 8px 20px -4px rgba(59, 130, 246, 0.5)",
                            "&:hover": {
                                background: "linear-gradient(135deg, #2563EB 0%, #1D4ED8 100%)",
                                transform: "translateY(-2px)",
                                boxShadow: "0 12px 24px -4px rgba(59, 130, 246, 0.6)",
                            }
                        }}
                    >
                        Start Exploring
                    </Button>

                    <Typography variant="caption" display="block" sx={{ mt: 3, color: "rgba(255,255,255,0.3)" }}>
                        By continuing, you agree to our Terms of Use and Privacy Policy.
                    </Typography>

                </Paper>
            </Box>
        </Fade>
    );
};
